import { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, TextField, Button, Typography, CircularProgress,
    Alert, Divider
} from "@mui/material";
import BarcodeScanner from "./BarcodeScanner";
import { getInventoryItemByBarcode, createInventoryLog } from "./InventoryApi";

const IssueStockDialog = ({ open, onClose, onSuccess }) => {

    const [item, setItem]           = useState(null);
    const [quantity, setQuantity]   = useState("");
    const [notes, setNotes]         = useState("");
    const [loading, setLoading]     = useState(false);
    const [searching, setSearching] = useState(false);
    const [error, setError]         = useState("");

    const handleReset = () => {
        setItem(null);
        setQuantity("");
        setNotes("");
        setError("");
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    // Called by BarcodeScanner on Enter or camera scan
    const handleScan = async (barcode) => {
        setSearching(true);
        setError("");
        setItem(null);
        const found = await getInventoryItemByBarcode(barcode);
        setSearching(false);
        if (found) {
            setItem(found);
        } else {
            setError("Item not found. Please do a Stock Receive first.");
        }
    };

    const handleQuantityChange = (e) => {
        const val = e.target.value;
        setQuantity(val);
        if (item && Number(val) > Number(item.current_stock)) {
            setError(`Only ${item.current_stock} available in stock.`);
        } else {
            setError("");
        }
    };

    const handleConfirmIssue = async () => {
        if (!quantity || isNaN(quantity) || Number(quantity) <= 0) {
            setError("Please enter a valid quantity.");
            return;
        }
        if (Number(quantity) > Number(item.current_stock)) {
            setError(`Only ${item.current_stock} available in stock.`);
            return;
        }
        setLoading(true);
        setError("");
        try {
            await createInventoryLog({
                item_id: item.id,
                type: "OUT",
                quantity: Number(quantity),
                description: notes.trim() || null,
            });
            onSuccess?.();
            handleClose();
        } catch (err) {
            setError("Something went wrong. Please try again.");
        }
        setLoading(false);
    };

    const isInsufficient = item && quantity && Number(quantity) > Number(item.current_stock);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>- Issue Stock</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>

                    {/* Scanner */}
                    <BarcodeScanner
                        onScan={handleScan}
                        disabled={searching || !!item}
                    />

                    {/* Searching indicator */}
                    {searching && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                                Looking up item...
                            </Typography>
                        </Box>
                    )}

                    {/* Item Found */}
                    {item && (
                        <>
                            <Divider />
                            <Box
                                bgcolor={item.current_stock <= item.min_stock ? "#fff3e0" : "#f5f5f5"}
                                borderRadius={1}
                                p={1.5}
                            >
                                <Typography variant="subtitle1" fontWeight={700}>{item.name}</Typography>
                                <Typography variant="body2" color="text.secondary" textTransform="capitalize">
                                    {item.category.replace("_", " ")} · Current Stock:&nbsp;
                                    <Typography
                                        component="span"
                                        fontWeight={700}
                                        color={item.current_stock <= item.min_stock ? "warning.main" : "success.main"}
                                    >
                                        {item.current_stock}
                                    </Typography>
                                </Typography>
                            </Box>

                            <TextField
                                label="Quantity to Issue"
                                value={quantity}
                                onChange={handleQuantityChange}
                                size="small"
                                type="number"
                                fullWidth
                                autoFocus
                                error={!!isInsufficient}
                            />
                            <TextField
                                label="Notes (optional)"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                size="small"
                                fullWidth
                                placeholder="e.g. Issued to housekeeping floor 2"
                            />
                        </>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                {item && (
                    <Button onClick={handleReset} disabled={loading}>
                        Scan Again
                    </Button>
                )}
                {item && (
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmIssue}
                        disabled={loading || !!isInsufficient || !quantity}
                    >
                        {loading ? <CircularProgress size={18} /> : "Confirm Issue"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default IssueStockDialog;